import React, { useMemo, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  useTheme
} from '@material-ui/core';
import {
  School,
  CheckCircle,
  Warning,
  Schedule,
  TrendingUp,
  Person,
  Assessment,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Chart } from 'react-google-charts';

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e',
  },
  kpiCard: {
    borderRadius: 12,
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
    },
  },
  chartCard: {
    borderRadius: 16,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  progressRing: {
    position: 'relative',
    width: 120,
    height: 120,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  progressRingText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  metricBox: {
    textAlign: 'center',
    padding: theme.spacing(1.5),
    margin: theme.spacing(0.5),
    borderRadius: 8,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  userTable: {
    maxHeight: 300,
    overflowY: 'auto',
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  chartContainer: {
    height: 400,
    width: '100%',
    marginTop: theme.spacing(2),
  },
  summaryStats: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    background: 'rgba(255,255,255,0.5)',
    borderRadius: 8,
  }
}));

const TrainingAnalytics = ({ trainingAdminData, trainingData, getTranslation = (text) => text, preferredLanguage = 'EN' }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  // Use a MutationObserver to apply dark styles directly as inline styles on
  // Google Charts tooltip nodes the moment they are added to the DOM.
  // CSS !important cannot beat Google Charts' own inline-style injection, but
  // setting element.style properties after the node appears always wins.
  useEffect(() => {
    if (!isDark) return;

    const applyDarkStyles = (node) => {
      if (!node || node.nodeType !== 1) return;

      const tooltip = node.classList?.contains('google-visualization-tooltip')
        ? node
        : node.querySelector?.('.google-visualization-tooltip');

      if (!tooltip) return;

      tooltip.style.setProperty('background', '#1e2a3a', 'important');
      tooltip.style.setProperty('border', '1px solid rgba(255,255,255,0.15)', 'important');
      tooltip.style.setProperty('border-radius', '8px', 'important');
      tooltip.style.setProperty('box-shadow', '0 4px 20px rgba(0,0,0,0.5)', 'important');
      tooltip.style.setProperty('padding', '8px 12px', 'important');
      tooltip.style.setProperty('z-index', '9999', 'important');
      tooltip.style.setProperty('position', 'absolute', 'important');
      // Google fixes the tooltip height before our padding is applied; the added
      // side padding rewraps long labels onto more lines, so the content grows
      // taller than that height and the last rows spill below the styled box.
      // Force the box to size to its content so everything stays inside it.
      tooltip.style.setProperty('height', 'auto', 'important');
      tooltip.style.setProperty('max-height', 'none', 'important');
      tooltip.style.setProperty('overflow', 'visible', 'important');
      tooltip.querySelectorAll('span, li, .google-visualization-tooltip-item').forEach(el => {
        el.style.setProperty('color', '#e0e0e0', 'important');
        el.style.setProperty('background', 'transparent', 'important');
      });
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(applyDarkStyles);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isDark]);

  console.log('TrainingAnalytics - trainingData:', trainingData);

  // Process training data
  const processedData = useMemo(() => {
    if (!trainingAdminData) return null;

    const clientStats = trainingAdminData.getClientTrainingStats || {};
    const userStats = trainingAdminData.getUserLevelTrainingStats || {};

    // Extract user data from the client ID key
    const clientId = clientStats.clientId || Object.keys(userStats)[0];
    const userData = userStats[clientId] || [];

    // Calculate user-level metrics
    const userMetrics = userData.map(user => {
      const assigned = user.trainingCoursesAssigned || [];
      const completed = user.trainingCoursesCompleted || [];
      const inProgress = user.trainingCoursesInProgress || [];
      
      const totalAssigned = assigned.length + completed.length + inProgress.length;
      const totalCompleted = completed.length;
      const totalInProgress = inProgress.length;
      const completionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : 0;
      
      // Check for overdue courses
      const now = new Date();
      const overdueCourses = assigned.filter(course => {
        if (!course.dueDate || course.completedDate) return false;
        return new Date(course.dueDate) < now;
      });
      
      // Check for upcoming deadlines (within 7 days)
      const upcomingDeadlines = assigned.filter(course => {
        if (!course.dueDate || course.completedDate) return false;
        const dueDate = new Date(course.dueDate);
        const daysUntilDue = (dueDate - now) / (1000 * 60 * 60 * 24);
        return daysUntilDue <= 7 && daysUntilDue > 0;
      });

      // Calculate average completion time
      const completionTimes = completed
        .filter(course => course.assignedDate && course.completedDate)
        .map(course => {
          const assigned = new Date(course.assignedDate);
          const complete = new Date(course.completedDate);
          return (complete - assigned) / (1000 * 60 * 60 * 24); // days
        });
      
      const avgCompletionTime = completionTimes.length > 0 
        ? completionTimes.reduce((sum, time) => sum + time, 0) / completionTimes.length 
        : 0;

      // Determine user status - prioritize completion over deadlines
      let userStatus = 'good';
      let statusColor = '#4caf50';
      
      if (totalAssigned === 0) {
        // No courses assigned
        userStatus = 'good';
        statusColor = '#4caf50';
      } else if (completionRate === 100) {
        // All assigned courses completed - COMPLIANT regardless of dates
        userStatus = 'good';
        statusColor = '#4caf50';
      } else if (overdueCourses.length > 0) {
        // Has incomplete courses that are overdue
        userStatus = 'overdue';
        statusColor = '#f44336';
      } else if (upcomingDeadlines.length > 0) {
        // Has incomplete courses due soon
        userStatus = 'upcoming';
        statusColor = '#ffc107';
      } else if (totalInProgress > 0) {
        // Has courses in progress
        userStatus = 'in-progress';
        statusColor = '#2196f3';
      } else {
        // Has assigned courses but none started
        userStatus = 'overdue';
        statusColor = '#f44336';
      }

      return {
        ...user,
        username: user.userName ? user.userName.split('@')[0] : 'Unknown',
        totalAssigned,
        totalCompleted,
        totalInProgress,
        completionRate,
        overdueCourses: overdueCourses.length,
        upcomingDeadlines: upcomingDeadlines.length,
        avgCompletionTime: Math.round(avgCompletionTime),
        userStatus,
        statusColor,
        completedCourses: completed
      };
    });

    // Calculate company-wide metrics from actual user data
    const totalUsers = userData.length;
    
    // Sum up actual user totals for accurate company metrics
    const totalAssignedCourses = userMetrics.reduce((sum, user) => sum + user.totalAssigned, 0);
    const totalCompletedCourses = userMetrics.reduce((sum, user) => sum + user.totalCompleted, 0);
    const totalInProgressCourses = userMetrics.reduce((sum, user) => sum + user.totalInProgress, 0);
    
    const overallCompletionRate = totalAssignedCourses > 0 
      ? (totalCompletedCourses / totalAssignedCourses) * 100 
      : 0;

    const usersAtRisk = userMetrics.filter(user => user.userStatus === 'overdue').length;
    const usersUpcoming = userMetrics.filter(user => user.userStatus === 'upcoming').length;
    const usersCompliant = userMetrics.filter(user => user.userStatus === 'good').length;

    // Sort users by risk level
    const sortedUsers = userMetrics.sort((a, b) => {
      if (a.userStatus === 'overdue' && b.userStatus !== 'overdue') return -1;
      if (b.userStatus === 'overdue' && a.userStatus !== 'overdue') return 1;
      if (a.userStatus === 'upcoming' && b.userStatus === 'good') return -1;
      if (b.userStatus === 'upcoming' && a.userStatus === 'good') return 1;
      return b.completionRate - a.completionRate;
    });

    // Process module-specific insights
    const moduleCompletions = new Map();
    const uniqueModulesSet = new Set();
    
    userMetrics.forEach(user => {
      // Count completed modules
      user.completedCourses.forEach(course => {
        const moduleId = course.trainingModuleId;
        if (moduleId) {
          uniqueModulesSet.add(moduleId);
          moduleCompletions.set(moduleId, (moduleCompletions.get(moduleId) || 0) + 1);
        }
      });
      
      // Also count assigned modules for unique count
      user.trainingCoursesAssigned.forEach(course => {
        const moduleId = course.trainingModuleId;
        if (moduleId) {
          uniqueModulesSet.add(moduleId);
        }
      });
    });
    
    // Create top modules list with titles from training data - ONLY include modules we have titles for
    const topModules = Array.from(moduleCompletions.entries())
      .map(([moduleId, completions]) => {
        // Look for the module in trainingData if available
        let moduleTitle = null;
        if (trainingData?.trainingItems) {
          const moduleItem = trainingData.trainingItems.find(
            item => item.modId === moduleId
          );
          if (moduleItem?.title) {
            moduleTitle = moduleItem.title;
          }
        }
        
        // Only return if we found a real title
        if (moduleTitle) {
          return { moduleId, moduleTitle, completions };
        }
        return null;
      })
      .filter(module => module !== null) // Remove modules without titles
      .sort((a, b) => b.completions - a.completions);

    return {
      clientStats,
      userMetrics,
      sortedUsers: sortedUsers.slice(0, 10), // Top 10 for display
      totalUsers,
      totalAssignedCourses,
      totalCompletedCourses,
      totalInProgressCourses,
      overallCompletionRate,
      usersAtRisk,
      usersUpcoming,
      usersCompliant,
      uniqueModules: uniqueModulesSet.size,
      topModules,
    };
  }, [trainingAdminData, trainingData]);

  // Generate enhanced timeline chart data with module breakdown
  const timelineChartData = useMemo(() => {
    if (!processedData || !processedData.userMetrics || !trainingData) return null;

    const now = new Date();
    const sixMonthsAgo = new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
    
    // Collect all completions with module details
    const allCompletions = [];
    const moduleSet = new Set();
    
    processedData.userMetrics.forEach(user => {
      user.completedCourses.forEach(course => {
        if (course.completedDate && course.trainingModuleId) {
          const completionDate = new Date(course.completedDate);
          if (completionDate >= sixMonthsAgo) {
            // Get module title - ONLY include if we have a real title
            let moduleTitle = null;
            if (trainingData?.trainingItems) {
              const moduleItem = trainingData.trainingItems.find(
                item => item.modId === course.trainingModuleId
              );
              if (moduleItem?.title) {
                // Truncate very long titles for legend display but keep them readable
                moduleTitle = moduleItem.title.length > 35 
                  ? moduleItem.title.substring(0, 32) + '...' 
                  : moduleItem.title;
              }
            }
            
            // Only add if we found a real module title
            if (moduleTitle) {
              allCompletions.push({
                date: completionDate,
                moduleId: course.trainingModuleId,
                moduleTitle: moduleTitle
              });
              
              moduleSet.add(moduleTitle);
            }
          }
        }
      });
    });

    if (allCompletions.length === 0) return null;

    // Create module columns sorted by popularity
    const modules = Array.from(moduleSet);
    const modulePopularity = {};
    allCompletions.forEach(completion => {
      modulePopularity[completion.moduleTitle] = (modulePopularity[completion.moduleTitle] || 0) + 1;
    });
    const sortedModules = modules.sort((a, b) => (modulePopularity[b] || 0) - (modulePopularity[a] || 0))
      .slice(0, 8); // Limit to top 8 modules for better legend readability

    // Group completions by month and module
    const monthlyModuleData = new Map();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      const moduleData = {};
      sortedModules.forEach(module => {
        moduleData[module] = 0;
      });
      
      monthlyModuleData.set(monthName, moduleData);
    }
    
    // Fill in actual completion data
    allCompletions.forEach(completion => {
      const monthKey = `${completion.date.getFullYear()}-${String(completion.date.getMonth() + 1).padStart(2, '0')}`;
      const date = new Date(completion.date.getFullYear(), completion.date.getMonth(), 1);
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      
      if (monthlyModuleData.has(monthName)) {
        const moduleData = monthlyModuleData.get(monthName);
        if (moduleData[completion.moduleTitle] !== undefined) {
          moduleData[completion.moduleTitle]++;
        }
      }
    });

    // Build chart data with module columns
    const chartData = [['Month', ...sortedModules]];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      const moduleData = monthlyModuleData.get(monthName);
      const row = [monthName, ...sortedModules.map(module => moduleData[module] || 0)];
      chartData.push(row);
    }

    return chartData;
  }, [processedData, trainingData]);

  const chartOptions = {
    backgroundColor: 'transparent',
    chartArea: { left: 60, top: 30, width: '80%', height: '75%' },
    colors: ['#0055A2'],
    curveType: 'function',
    lineWidth: 3,
    pointSize: 6,
    pointShape: 'circle',
    legend: { position: 'none' },
    hAxis: {
      textStyle: { color: theme.palette.text.secondary, fontSize: 11 },
      gridlines: { color: theme.palette.divider, count: 6 }
    },
    vAxis: {
      textStyle: { color: theme.palette.text.secondary, fontSize: 11 },
      minValue: 0,
      gridlines: { color: theme.palette.divider },
      title: getTranslation('Courses Completed', preferredLanguage),
      titleTextStyle: { color: theme.palette.text.primary, fontSize: 12 }
    },
    animation: {
      startup: true,
      duration: 1000,
      easing: 'out',
    },
  };

  const getStatusChip = (user) => {
    const statusConfig = {
      'overdue': { label: getTranslation('OVERDUE', preferredLanguage), color: '#f44336' },
      'upcoming': { label: getTranslation('DUE SOON', preferredLanguage), color: '#ffc107' },
      'in-progress': { label: getTranslation('IN PROGRESS', preferredLanguage), color: '#2196f3' },
      'good': { label: getTranslation('COMPLIANT', preferredLanguage), color: '#4caf50' }
    };
    
    const config = statusConfig[user.userStatus] || statusConfig.good;
    
    return (
      <Chip
        size="small"
        label={config.label}
        className={classes.statusChip}
        style={{
          backgroundColor: `${config.color}20`,
          color: config.color
        }}
      />
    );
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toString();
  };

  if (!trainingAdminData || !processedData) {
    return (
      <Box>
        <Typography variant="h5" className={classes.sectionTitle}>
          Training Analytics
        </Typography>
        <Paper style={{ padding: 32, textAlign: 'center', marginTop: 16 }}>
          <Typography variant="body1" color="textSecondary">
            No training data available. Training analytics will appear when training courses are assigned to users.
          </Typography>
        </Paper>
      </Box>
    );
  }

  console.log(processedData.sortedUsers)

  return (
    <Box>
      {/* Section Title */}
      <Typography variant="h5" className={classes.sectionTitle}>
        Training Analytics
      </Typography>

      {/* Top Row - Two Half-Width Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Training Completion Overview */}
        <Grid item xs={12} md={6}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <School style={{ color: '#0055A2', fontSize: 24, marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Training Completion Overview
                </Typography>
              </Box>

              {/* Playing Card #5 Layout: Circle in center, metrics in corners */}
              <Box style={{ 
                position: 'relative', 
                height: 160, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginBottom: 8,
                padding: '0 12px'
              }}>
                {/* Center Circle Chart */}
                <Box style={{ 
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r="48"
                      fill="none"
                      stroke="rgba(0, 85, 162, 0.1)"
                      strokeWidth="8"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="48"
                      fill="transparent"
                      stroke="#0055A2"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 48}`}
                      strokeDashoffset={`${2 * Math.PI * 48 * (1 - processedData.overallCompletionRate / 100)}`}
                      style={{ transition: 'stroke-dashoffset 1s ease' }}
                    />
                  </svg>
                  <Box style={{
                    position: 'absolute',
                    textAlign: 'center',
                    zIndex: 1
                  }}>
                    <Typography variant="h4" style={{ fontWeight: 700, color: '#0055A2', fontSize: '2rem' }}>
                      {Math.round(processedData.overallCompletionRate)}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                      Complete
                    </Typography>
                  </Box>
                </Box>

                {/* Top Left: Completed */}
                <Box style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  textAlign: 'center',
                  background: 'rgba(76, 175, 80, 0.12)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  border: '1px solid rgba(76, 175, 80, 0.25)',
                  width: 80
                }}>
                  <Typography variant="h6" style={{ color: '#4caf50', fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatNumber(processedData.totalCompletedCourses)}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#4caf50' }}>{getTranslation('Completed', preferredLanguage)}</Typography>
                </Box>

                {/* Top Right: In Progress */}
                <Box style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  textAlign: 'center',
                  background: 'rgba(33, 150, 243, 0.12)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  border: '1px solid rgba(33, 150, 243, 0.25)',
                  width: 80
                }}>
                  <Typography variant="h6" style={{ color: '#2196f3', fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatNumber(processedData.totalInProgressCourses)}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#2196f3' }}>{getTranslation('In Progress', preferredLanguage)}</Typography>
                </Box>

                {/* Bottom Left: Remaining */}
                <Box style={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  textAlign: 'center',
                  background: 'rgba(255, 152, 0, 0.12)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  border: '1px solid rgba(255, 152, 0, 0.25)',
                  width: 80
                }}>
                  <Typography variant="h6" style={{ color: '#ff9800', fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatNumber(processedData.totalAssignedCourses - processedData.totalCompletedCourses)}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#ff9800' }}>{getTranslation('Remaining', preferredLanguage)}</Typography>
                </Box>

                {/* Bottom Right: Modules */}
                <Box style={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  textAlign: 'center',
                  background: 'rgba(156, 39, 176, 0.12)',
                  borderRadius: 6,
                  padding: '6px 10px',
                  border: '1px solid rgba(156, 39, 176, 0.25)',
                  width: 80
                }}>
                  <Typography variant="h6" style={{ color: '#9c27b0', fontWeight: 700, fontSize: '1.1rem' }}>
                    {formatNumber(processedData.uniqueModules)}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#9c27b0' }}>{getTranslation('Modules', preferredLanguage)}</Typography>
                </Box>
              </Box>

              {/* Module Insights */}
              <Box style={{ 
                marginTop: 12, 
                padding: 12, 
                background: isDark
                  ? theme.palette.background.default
                  : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.8) 100%)', 
                borderRadius: 8,
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0, 85, 162, 0.1)',
                boxShadow: '0 1px 4px rgba(0,85,162,0.06)'
              }}>
                <Box display="flex" alignItems="center" marginBottom={4}>
                  <Box 
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #0055A2 0%, #428BC9 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 6
                    }}
                  >
                    <Typography style={{ color: 'white', fontSize: '0.7rem', fontWeight: 700 }}>
                      ★
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" style={{ 
                    fontWeight: 600, 
                    color: isDark ? theme.palette.text.primary : '#0055A2',
                    fontSize: '0.85rem'
                  }}>
                    Top Performing Modules
                  </Typography>
                </Box>
                {processedData.topModules && processedData.topModules.length > 0 ? (
                  processedData.topModules.slice(0, 3).map((module, index) => (
                    <Box key={module.moduleId} 
                         style={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: 'center',
                           padding: '6px 10px',
                           marginBottom: index < 2 ? 4 : 0,
                           background: index === 0
                             ? (isDark ? 'rgba(0, 85, 162, 0.25)' : 'rgba(0, 85, 162, 0.08)')
                             : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)'),
                           borderRadius: 6,
                           border: index === 0
                             ? '1px solid rgba(0, 85, 162, 0.35)'
                             : (isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)'),
                           transition: 'all 0.2s ease',
                           minHeight: 36
                         }}>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" style={{ 
                          fontSize: '0.75rem', 
                          fontWeight: index === 0 ? 600 : 500,
                          color: isDark ? theme.palette.text.primary : '#333',
                          lineHeight: 1.2,
                          marginBottom: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {module.moduleTitle}
                        </Typography>
                        <Typography variant="caption" style={{ 
                          color: isDark ? theme.palette.text.secondary : '#666',
                          fontSize: '0.65rem'
                        }}>
                        {index === 0 ? `🏆 ${getTranslation('Most Popular', preferredLanguage)}` : `#${index + 1} ${getTranslation('Most Popular', preferredLanguage)}`}
                        </Typography>
                      </Box>
                      <Box style={{ 
                        minWidth: 32,
                        height: 20,
                        background: index === 0 ? 'linear-gradient(135deg, #0055A2 0%, #428BC9 100%)' : 'rgba(0, 85, 162, 0.8)',
                        borderRadius: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: 8
                      }}>
                        <Typography style={{ 
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 700
                        }}>
                          {module.completions}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box style={{
                    textAlign: 'center',
                    padding: '12px 0',
                    color: isDark ? theme.palette.text.secondary : '#666'
                  }}>
                    <Typography variant="caption" style={{ fontSize: '0.8rem' }}>
                      📚 {getTranslation('No completed modules yet', preferredLanguage)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Compliance & At-Risk Users */}
        <Grid item xs={12} md={6}>
          <Card className={classes.kpiCard}>
            <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <Assessment style={{ color: '#f44336', fontSize: 24, marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  Compliance & Risk Status
                </Typography>
              </Box>

              {/* Status Breakdown */}
              <Grid container spacing={1} style={{ marginBottom: 16 }}>
                <Grid item xs={4}>
                  <Box className={classes.metricBox} style={{ backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
                    <Typography variant="h5" style={{ color: '#f44336', fontWeight: 700 }}>
                      {processedData.usersAtRisk}
                    </Typography>
                    <Typography variant="caption">{getTranslation('Overdue', preferredLanguage)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box className={classes.metricBox} style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                    <Typography variant="h5" style={{ color: '#ffc107', fontWeight: 700 }}>
                      {processedData.usersUpcoming}
                    </Typography>
                    <Typography variant="caption">{getTranslation('Due Soon', preferredLanguage)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box className={classes.metricBox} style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
                    <Typography variant="h5" style={{ color: '#4caf50', fontWeight: 700 }}>
                      {processedData.usersCompliant}
                    </Typography>
                    <Typography variant="caption">{getTranslation('Compliant', preferredLanguage)}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Top Users Table */}
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                User Status (Top 10)
              </Typography>
              
              <TableContainer className={classes.userTable} style={{ flex: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{getTranslation('User', preferredLanguage)}</strong></TableCell>
                      <TableCell align="center"><strong>{getTranslation('Progress', preferredLanguage)}</strong></TableCell>
                      <TableCell align="center"><strong>{getTranslation('Status', preferredLanguage)}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {processedData.sortedUsers.map((user, index) => (
                      <TableRow key={user.userName || index}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar style={{ width: 24, height: 24, fontSize: 12 }}>
                              {user.username[0]?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {user.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Typography variant="body2" style={{ fontSize: '0.8rem', marginBottom: 2 }}>
                              {user.totalCompleted}/{user.totalAssigned}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={user.completionRate}
                              style={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0,0,0,0.1)'
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(user)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced PhishCloud Timeline Chart */}
      <Paper style={{
        borderRadius: 16,
        background: 'linear-gradient(135deg, #0055A2 0%, #428BC9 100%)',
        color: 'white',
        padding: 24,
        marginTop: 24,
        boxShadow: '0 8px 32px rgba(0, 85, 162, 0.3)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
        }
      }}>
        <Box display="flex" alignItems="center" marginBottom={3}>
          <Box 
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12
            }}
          >
            <TrendingUp style={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 700, color: 'white', marginBottom: 4 }}>
              📈 {getTranslation('Training Progress Timeline', preferredLanguage)}
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
              {getTranslation('6-month view', preferredLanguage)} • {getTranslation('Course completions & module engagement trends', preferredLanguage)}
            </Typography>
          </Box>
        </Box>

        <Box style={{
          background: isDark ? 'rgba(30,42,58,0.95)' : 'rgba(255,255,255,0.95)',
          borderRadius: 12,
          padding: 16,
          height: 320,
          marginBottom: 16
        }}>
          {timelineChartData && timelineChartData.length > 1 ? (
            <Chart
              chartType="AreaChart"
              data={timelineChartData}
              options={{
                backgroundColor: 'transparent',
                chartArea: { left: 60, top: 30, width: '80%', height: '70%' },
                colors: ['#0055A2', '#428BC9', '#FFD700', '#FF6B9D', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'],
                isStacked: true,
                curveType: 'function',
                lineWidth: 0,
                areaOpacity: 0.8,
                legend: { 
                  position: 'right',
                  alignment: 'start',
                  textStyle: { color: isDark ? '#e0e0e0' : '#333', fontSize: 10, fontName: 'Montserrat' },
                  maxLines: 8
                },
                hAxis: {
                  textStyle: { color: isDark ? '#e0e0e0' : '#666', fontSize: 11 },
                  gridlines: { color: '#f0f0f0', count: 6 },
                  format: 'MMM yyyy'
                },
                vAxis: {
                  textStyle: { color: isDark ? '#e0e0e0' : '#666', fontSize: 11 },
                  gridlines: { color: '#f0f0f0' },
                  minValue: 0,
                  title: 'Completions',
                  titleTextStyle: { color: isDark ? '#e0e0e0' : '#333', fontSize: 12 }
                },
                animation: {
                  startup: true,
                  duration: 1200,
                  easing: 'out',
                },
                tooltip: {
                  isHtml: true,
                  textStyle: { color: isDark ? '#e0e0e0' : '#333', fontSize: 12 }
                },
                focusTarget: 'category'
              }}
              width="100%"
              height="100%"
              loader={<div style={{ color: '#666' }}>{getTranslation('Loading training timeline...', preferredLanguage)}</div>}
            />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
              <Typography variant="body2" style={{ color: '#666' }}>
                📊 {getTranslation('No training data available for timeline analysis', preferredLanguage)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Enhanced Summary Stats */}
        <Box style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 12,
          padding: 16
        }}>
          <Box textAlign="center">
            <Box style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px'
            }}>
              <Typography style={{ fontSize: '1.2rem' }}>🏆</Typography>
            </Box>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
              Total Completions
            </Typography>
            <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
              {timelineChartData ? timelineChartData.slice(1).reduce((sum, row) => sum + row[1], 0) : 0}
            </Typography>
          </Box>
          
          <Box textAlign="center">
            <Box style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px'
            }}>
              <Typography style={{ fontSize: '1.2rem' }}>📈</Typography>
            </Box>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
              Monthly Average
            </Typography>
            <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
              {timelineChartData ? Math.round(timelineChartData.slice(1).reduce((sum, row) => sum + row[1], 0) / 6) : 0}
            </Typography>
          </Box>
          
          <Box textAlign="center">
            <Box style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px'
            }}>
              <Typography style={{ fontSize: '1.2rem' }}>🚀</Typography>
            </Box>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
              Peak Month
            </Typography>
            <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
              {timelineChartData ? Math.max(...timelineChartData.slice(1).map(row => row[1])) : 0}
            </Typography>
          </Box>
          
          <Box textAlign="center">
            <Box style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px'
            }}>
              <Typography style={{ fontSize: '1.2rem' }}>📚</Typography>
            </Box>
            <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
              Active Modules
            </Typography>
            <Typography variant="h5" style={{ fontWeight: 700, color: 'white' }}>
              {processedData ? processedData.uniqueModules : 0}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TrainingAnalytics;